import { AnyAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit';
import {ITeamStandingsItem, EventState, TechnicalSortType, TimeTableEventTabType, ITeamStandingsData} from '@App/types';
import { RootState } from '@App/store';

import teamStandingsApi from '@App/services/teamStandings/teamStandingsApi';
import { setActiveTab } from '@Features/timeTableSelectedEvent/timeTableSelectedEventSlice';
import { setSelectedTimeTableId } from '@Features/timeTable/timeTableSlice';
import { eventsApi, EventsStatesResponse } from '@App/services/events/eventsApi';
import { pollingSucceeded, pollingFailed } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

import { pollingErrorHandlerSlice } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

import { selectTechnicalSort } from '@App/appUserStateSlice';

export interface IGetTeamStandingsDataPayload {
  timeTableId: number;
}

export interface ITeamStandingsState {
  changeTimestamp: number; // Tohle trigeruje mount/unmout pooling kontejneru, tak aby se forsnul reload při znovu kliku (jinak se vysypal state)
  dataLoaded: boolean;
  isFetching: boolean;
  error: any;
  data: ITeamStandingsData | null;
}

const initialState: ITeamStandingsState = {
  changeTimestamp: 0,
  dataLoaded: false,
  isFetching: false,
  error: null,
  data: null,
};

// Toto je big hack jak se v thunku dostat ke stavu z RTK query, bohužel u callbacku z PollingContaineru vznikaly jakési divné "capture scope" kdy tam byly jiné hodnoty
const selectEventStatesData = (state: RootState) => state.eventsApi.queries['getEventsStates("")']?.data as EventsStatesResponse;

export const getTeamStandingsDataAsync = createAsyncThunk(
  'teamStandings/fetchData',
  async ({ timeTableId }: IGetTeamStandingsDataPayload, { dispatch, rejectWithValue, signal, getState }) => {
    try {
      const state = getState() as RootState;
      const eventStatesData = selectEventStatesData(state);
      const eventState = eventStatesData.states[timeTableId]?.state || EventState.Unofficial;

      const response = await teamStandingsApi.fetchTeamStandingsData(timeTableId, signal);

      const thunkDispatch = dispatch as ThunkDispatch<any, any, AnyAction>; // Nutno to přetypovat, TypeScript si s tím nějak
      thunkDispatch(eventsApi.util.updateQueryData('getEventsStates', '', draft => {
        draft.states[timeTableId] = response.eventState;
      }));

      dispatch(pollingSucceeded());

      return response.data;
    } catch (err) {
      dispatch(pollingFailed({ sectionName: 'TeamStandings', error: err }));
      return rejectWithValue(err);
    }
  }
);

export const teamStandingsSlice = createSlice({
  name: 'teamStandingsSlice',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTeamStandingsDataAsync.pending, state => {
        state.isFetching = true;
      })
      .addCase(getTeamStandingsDataAsync.fulfilled, (state, action) => {
        state.isFetching = false;
        state.dataLoaded = true;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(getTeamStandingsDataAsync.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.isFetching = false;
          state.error = action.payload;
        }
      })
      .addCase(setActiveTab, (state, action) => { // Clear state
        if (action.payload === TimeTableEventTabType.Results) {
          state.changeTimestamp = Date.now();
        } else {
          Object.assign(state, initialState);
        }
      })
      .addCase(setSelectedTimeTableId, (state, action) => {
        if (action.payload.hasChanged) {
          Object.assign(state, initialState);
        } else {
          state.changeTimestamp = Date.now();
        }
      })
      .addCase(eventsApi.internalActions.unsubscribeQueryResult, state => { // Clear state when movind to different screen
        Object.assign(state, initialState);
      })
      .addCase(pollingErrorHandlerSlice.actions.clearPollingErrors, (state) => {
        state.error = null;
      });
  }
});

export const selectIsFetching = (state: RootState) => state.teamStandings.isFetching;
export const selectError = (state: RootState) => state.teamStandings.error;
export const selectDataLoaded = (state: RootState) => state.teamStandings.dataLoaded;
export const selectData = (state: RootState) => state.teamStandings.data;
export const selectChangeTimestamp = (state: RootState) => state.teamStandings.changeTimestamp;

export default teamStandingsSlice.reducer;